import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import clsx from 'clsx';
import Icon from '../Icon';
import { chartPalette, networkPalette, tooltipContentStyle } from '../../utils/chartTheme';

const NETWORK_LABELS = { base: 'Base', polygon: 'Polygon', solana: 'Solana' };

const Aggregate = ({ label, value, accent }) => (
  <div>
    <div className="text-xs" style={{ color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: accent || 'var(--tx1)', marginTop: 4 }}>{value}</div>
  </div>
);

const RIEEfficiency = ({ routes = [], summary = {} }) => {
  const totalRouted = (routes || []).reduce((sum, r) => sum + parseInt(r.count, 10), 0);

  const pieData = (routes || []).map((r) => ({
    name: NETWORK_LABELS[r.route_selected] || r.route_selected,
    value: parseInt(r.count, 10),
    color: networkPalette[r.route_selected] || chartPalette.teal,
  }));

  const fallbackPct = (summary.total_count || 0) > 0
    ? ((summary.fallback_count / summary.total_count) * 100).toFixed(1)
    : '0';
  const fallbackAccent = (summary.fallback_count || 0) > 5 ? 'var(--warn)' : 'var(--ok)';

  return (
    <div className="card" style={{ gridColumn: 'span 2' }}>
      <div className="row row-gap-3 mb-3">
        <Icon name="dashboard" size={18} style={{ color: 'var(--teal)' }} />
        <div className="card-title" style={{ margin: 0 }}>RIE — Routing Intelligence Engine</div>
      </div>

      {(!routes || routes.length === 0) ? (
        <div className="empty" style={{ padding: 'var(--s8) var(--s4)' }}>
          <div className="empty-icon"><Icon name="dashboard" size={22} /></div>
          <h3 style={{ fontSize: 14 }}>No routing activity yet</h3>
          <p>Once the RIE picks chains for the next batch of transactions, network share and gas metrics will appear here.</p>
        </div>
      ) : (
      <div className="rie-grid">
        {/* Pie */}
        <div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%" cy="50%"
                innerRadius={56}
                outerRadius={90}
                paddingAngle={3}
                dataKey="value"
                stroke="none"
              >
                {pieData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipContentStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="text-xs" style={{ textAlign: 'center', color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginTop: 4 }}>
            Route distribution
          </div>
        </div>

        {/* Table + aggregate */}
        <div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Network</th>
                  <th>Txns</th>
                  <th>Share</th>
                  <th>Avg gas</th>
                  <th>Latency</th>
                  <th>Fallback</th>
                  <th>Failures</th>
                </tr>
              </thead>
              <tbody>
                {(routes || []).map((route, i) => {
                  const share = totalRouted > 0 ? ((route.count / totalRouted) * 100).toFixed(1) : '0';
                  const colour = networkPalette[route.route_selected] || chartPalette.teal;
                  return (
                    <tr key={i}>
                      <td style={{ color: 'var(--tx1)', fontWeight: 600 }}>
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 9999, background: colour, marginRight: 8 }} />
                        {NETWORK_LABELS[route.route_selected] || route.route_selected}
                      </td>
                      <td>{route.count}</td>
                      <td>{share}%</td>
                      <td>${parseFloat(route.avg_gas_cost).toFixed(4)}</td>
                      <td>{parseInt(route.avg_latency_ms, 10)}ms</td>
                      <td>
                        <span className={clsx('badge', route.fallback_count > 0 ? 'badge-warning' : 'badge-muted')}>
                          {route.fallback_count}
                        </span>
                      </td>
                      <td>
                        <span className={clsx('badge', route.failed_count > 0 ? 'badge-danger' : 'badge-success')}>
                          {route.failed_count}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 'var(--s6)', marginTop: 'var(--s4)', paddingTop: 'var(--s3)', borderTop: '1px solid var(--bd)' }}>
            <Aggregate
              label="Total paymaster spend"
              value={`$${(parseFloat(summary.avg_gas_cost || 0) * parseInt(summary.total_count || 0, 10)).toFixed(2)}`}
              accent="var(--teal)"
            />
            <Aggregate
              label="Avg routing latency"
              value={`${parseInt(summary.avg_latency_ms || 0, 10)}ms`}
            />
            <Aggregate
              label="Fallback rate"
              value={`${fallbackPct}%`}
              accent={fallbackAccent}
            />
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default RIEEfficiency;
